import { FC, memo, useContext, useMemo } from 'react';
import { Typography } from 'antd';
import { useSnapshot } from 'valtio';
import type { UIArTable } from '#types/System.types';

import { PageScopeContext, formStore } from '../../store';
import { useEvaluateFormAccess } from '../../use/data/useEvaluateFormAccess';
import { resolveContextRoutes } from '../../services/contextRouting';
import ArTable from './ArTable';
import { useVisibilityMode } from '../../use/useVisibilityMode';

const { Text } = Typography;

interface ArTableUIProps {
  uiArTable: UIArTable;
  disabled?: boolean;
}

const ArTableUI: FC<ArTableUIProps> = ({ uiArTable, disabled = false }) => {
  const pageScope = useSnapshot(useContext(PageScopeContext));
  const formSnap = useSnapshot(formStore.state);
  const visibilityMode = useVisibilityMode();

  const formId = formSnap.formId;
  const versionId = formSnap.versionId;
  const insideFormContext = formSnap.formId !== undefined;

  const entityPreview = pageScope.source === 'preview' ? pageScope.preview : null;
  const entityId = entityPreview
    ? entityPreview.entityId
    : pageScope.source === 'entity' && pageScope.entityId !== ''
      ? pageScope.entityId
      : undefined;
  const recordId = entityPreview
    ? entityPreview.recordId
    : pageScope.source === 'entity'
      ? pageScope.recordId
      : undefined;
  const insideEntityContext = entityPreview
    ? entityPreview.insideEntityRecordContext
    : !!entityId;

  const access = useEvaluateFormAccess({ formId, versionId, enabled: insideFormContext });
  const formReadOnly = !access.canWrite;
  const entityLockedInContext = insideFormContext ? formReadOnly : false;

  const parentPath = useMemo(() => {
    const { docRef, path } = resolveContextRoutes({
      insideForm: insideFormContext,
      insideEntity: insideEntityContext,
      formId,
      versionId,
      entityId,
      recordId,
    });
    return docRef ? path : null;
  }, [entityId, formId, insideEntityContext, insideFormContext, recordId, versionId]);

  const effectiveDisabled = disabled || (insideFormContext && formReadOnly) || entityLockedInContext;

  if (!parentPath) {
    return <Text type="secondary" aria-label="table-unavailable">Table not available in this context</Text>;
  }

  return (
    <ArTable
      tableId={uiArTable.tableId}
      parentPath={parentPath}
      disabled={effectiveDisabled}
      visibilityMode={visibilityMode}
      columnConfig={uiArTable.columnConfig}
    />
  );
};

ArTableUI.displayName = 'ArTableUI';

export default memo(ArTableUI);
